import React, { useState, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faEye } from '@fortawesome/free-solid-svg-icons';
// eslint-disable-next-line import/no-unresolved
import { MODULE_FIELD_TYPES, OVERRIDE_FIELDS } from '../../constants'
import { detailSize, handleToolTipMouseEnter } from '../../helpers/layout'
import DeclineIcon from './DeclineIcon'
import SuccessIcon from './SuccessIcon'
import HTMLViewer from './HTMLViewer'
import TextShowMore from './TextShowMore'
import { validURL } from "../../helpers/formValidation"
import { PercentFormat, CurrencyFormat, DateFormat, TimeFormat } from '../../helpers/formatting'
import ViewSensitivePiiButtons from "../ViewSensitivePiiButtons";
import OverrideTag from './OverrideTag'

export default function DisplayField(props) {
  const {
    data,
    row,
    dropdowns,
    dropdownOptions
  } = props

  const [width, setWidth] = useState(0)
  const ref = useRef(null)
  const fieldType = dropdownOptions[data.module_field_type_dropdown_option_id] && dropdownOptions[data.module_field_type_dropdown_option_id].code_key

  useEffect(() => {
    setWidth(ref.current.clientWidth)
  })
  useEffect(() => {
    const getListSize = () => {
      const newWidth = ref.current?.clientWidth
      if (newWidth) setWidth(newWidth)
    }

    window.addEventListener('resize', getListSize)
    return () => window.removeEventListener('resize', getListSize)
  }, [])

  const displayRowData = () => {
    let val = row[data.column_name]

    if ((data.module_code_key in OVERRIDE_FIELDS)
      && (data.column_name in OVERRIDE_FIELDS[data.module_code_key])
      && row[OVERRIDE_FIELDS[data.module_code_key][data.column_name].override_field] !== null
      && row[OVERRIDE_FIELDS[data.module_code_key][data.column_name].override_source_field] !== null
    ) {
      return <OverrideTag value={val} sourceCodeKeyId={row[OVERRIDE_FIELDS[data.module_code_key][data.column_name].override_source_field]} />
    }

    //For Document type is Loan Agreement - display button to view the loan agreement as sensitive data
    if (data.module_code_key === 'document' && fieldType === MODULE_FIELD_TYPES.S3_LINK.code_key) {
      const isBankruptcyDocument = row.document_type_dropdown_option_id &&
        dropdownOptions.some((o) => o.code_key.startsWith('document_type_bankruptcy') && o.id === row.document_type_dropdown_option_id);
      const isLoanAgreement = row.document_type_dropdown_option_id &&
        dropdownOptions.some((o) => o.code_key === 'document_type_loan_agreement' && o.id === row.document_type_dropdown_option_id);
      let documentType = 'general_document';
      if (isBankruptcyDocument) documentType = 'bankruptcy_document';
      else if (isLoanAgreement) documentType = 'loan_agreement';
      return (
          <ViewSensitivePiiButtons
            customerId={row.customer_id}
            documentId={row.id}
            documentTypeId={row.document_type_dropdown_option_id}
            type={documentType}
            style={{ height: '20px' }}
          />
        );
    }

    // If NULL value display a dash for page clarity
    if (!val && val !== 0 && data.column_name !== 'tags' && fieldType !== MODULE_FIELD_TYPES.HTML.code_key) {
      return '-'
    }

    // eslint-disable-next-line default-case
    switch (fieldType) {
      case MODULE_FIELD_TYPES.DROPDOWN_SYSTEM.code_key:
        const dropdown_option = dropdowns.data
          .find((x) => x.code_key === data.dropdown_key)
          .dropdown_options.find((x) => x.id == val)
        val =
          dropdown_option && dropdown_option.name ? dropdown_option.name : '-'
        break

      case MODULE_FIELD_TYPES.DROPDOWN_SEARCHABLE.code_key:
      case MODULE_FIELD_TYPES.DROPDOWN.code_key:
        const field_label = data.column_name.replace('_id', '_label')
        let data_field = row[field_label];

        if (data_field && data.module_code_key === 'user' && data.column_name === 'user_role_id') {
          const isActiveColumn = data.column_name.replace('_id', '_is_active')
          if (row[isActiveColumn] === false || row[isActiveColumn] === 0) {
            data_field += ' (Inactive)'
          }
        }

        if (data_field) {
          val = (
            <a href={`/${data.dropdown_relation}/${row[data.column_name]}`}>
              {data_field}
            </a>
          )
        } else {
          val = (
            <a href={`/${data.dropdown_relation}/${row[data.column_name]}`}>
              {val}
            </a>
          )
        }
        break

      case MODULE_FIELD_TYPES.DATE.code_key:
        if (val) {
          val = <DateFormat dateString={val} />
        }
        break
      case MODULE_FIELD_TYPES.DATETIME.code_key:
        if (val) {
          val = <DateFormat dateString={val} formatString="MM/dd/yyyy hh:mm:ss a" />
        }
        break
      case MODULE_FIELD_TYPES.TIME.code_key:
        if (val) {
          val = <TimeFormat timeString={val} formatString="hh:mm:ss a" />
        }
        break

      case MODULE_FIELD_TYPES.CURRENCY.code_key:
        if (val) {
          val = <CurrencyFormat value={val} />
        }
        break

      case MODULE_FIELD_TYPES.PERCENT.code_key:
        if (val && ['apr', 'min_apr', 'max_apr', 'interest_rate'].includes(data.column_name)) {
          val = <PercentFormat value={val} fractionDigits={3} />
        } else if (val) {
          val = <PercentFormat value={val} />
        }
        break

      case MODULE_FIELD_TYPES.BOOLEAN.code_key:
        if (val === 1) {
          val = <SuccessIcon />
        }
        if (val === 0) {
          val = <DeclineIcon />
        }
        break

      case MODULE_FIELD_TYPES.HTML.code_key:
        val = val == null ? '' : val
        val = <HTMLViewer text={val} width={width} title={data.label_name} column_name={data.column_name} record={row} module_code_key={data.module_code_key} />
        break

      case MODULE_FIELD_TYPES.S3_LINK.code_key:
        if (validURL(val)) {
          val = (
            <a
              className="button is-primary"
              href={val}
              style={{
                height: '1.5rem'
              }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faEye} />
            </a>
          )
        }
        break

      case MODULE_FIELD_TYPES.URL.code_key:
        if (validURL(val)) {
          val = <a href={val} target="_blank" rel="noreferrer">{val}</a>
        }
        break

      case MODULE_FIELD_TYPES.ENCRYPTED.code_key:
        const last_four = `${data.column_name}_last_four`
        const max_length = data.max_length ? data.max_length : 10

        let data_field_last_four = row[last_four];

        if (data_field_last_four) {
          val = '#'.repeat(max_length - 4) + data_field_last_four
        } else {
          val = '#'.repeat(max_length)
        }
        break

      case MODULE_FIELD_TYPES.TAG_CLOUD.code_key:
        val = (!val || val.length === 0) ? '-' : (
          <span className="tags pt-1 pb-0">
            {val.map((item, index) => (
              <span className="tag is-link is-rounded" style={{ marginBottom: "0.4rem", padding: "0.5em", height: "1.2em", }} key={item.id}>{item.name}</span>
            ))}
          </span>
        )
        break

      default:
        break
    }

    return <TextShowMore text={val} width={width} />
  }
  const last_four = `${data.column_name}_last_four`
  const encrypted = `${data.column_name}_encrypted`

  const is_oel = row?.oel_fields?.find((x) => (x === data.column_name || x === last_four || x === encrypted)) ? true : false

  const buttonLabel = () => {
    if (data.module_code_key === 'customer' && data.column_name === 'ssn') {
      return (
        <ViewSensitivePiiButtons customerId={row.id} type={'full_ssn'} style={{ height: '23px' }} />
      )
    }

    if (data.module_code_key === 'customer' && data.column_name === 'bank_account_number') {
      return (
        <ViewSensitivePiiButtons customerId={row.id} type={'full_bank_account_number'} style={{ height: '23px' }} />
      )
    }

    if (data.module_code_key === 'bank_account' && data.column_name === 'bank_account_number') {
      return (
        <ViewSensitivePiiButtons customerId={row.customer_id} type={'full_bank_account_number'} style={{ height: '23px' }} bankAccountId={row.id} />
      )
    }
    return '';
  };

  return (
    <div
      className={`lox-display-field ${detailSize(
        data.detail_size
      )} column py-2 ${is_oel && "highlighted-box"}`}
    >
      <p className="lox-display-field__label has-text-grey is-small is-size-8">
        {data.label_name}
        <span
          className={`${data.helper_text ? 'lox-no-border' : 'is-hidden'} ml-1 lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary`}
          data-tooltip={data.helper_text}
          onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-detail')}
        >
          <FontAwesomeIcon
            icon={faQuestionCircle}
            className="has-text-link"
          />
        </span>
        {buttonLabel()}
      </p>
      <p ref={ref} className="lox-display-field__data has-text-dark">
        {displayRowData()}
      </p>
    </div>
  )
}
